import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import SideBar from "./SideBar";

const API_URL = process.env.REACT_APP_API_URL;

const UpdateUser = () => {
  const { userId } = useParams();
  const [email, setEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [location, setLocation] = useState("");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  const handleRoleChange = (selectedRoles) => {
    setRoles(selectedRoles.map((role) => role.value));
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = await localforage.getItem("token");
        const response = await api.get(`${API_URL}/auth/users/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const userData = response.data;
        setEmail(userData.email || "");
        setUserName(userData.userName || "");
        setLocation(userData.location || "");
        setRoles(userData.roles || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user:", error);
        setError("Failed to fetch user");
        setLoading(false);
      }
    };

    if (userId) {
      fetchUser();
    } else {
      setError("User ID is missing");
      setLoading(false);
    }
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      await api.put(`${API_URL}/auth/update-user/${userId}`, {
        userName,
        email,
        roles,
        location,
      });
      setSuccess("User updated successfully!");
      setTimeout(() => {
        navigate("/userdashboard");
      }, 2000);
    } catch (error) {
      console.error("Update failed:", error.response?.data?.message || error.message);
      setError("Failed to update user. Please try again.");
    }
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      border: state.isFocused ? "2px solid var(--primary-color)" : "2px solid var(--border-color)",
      borderRadius: "8px",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(206, 69, 43, 0.1)" : "none",
      cursor: "pointer",
      padding: "4px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "rgba(206, 69, 43, 0.1)",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "var(--primary-color)",
      fontWeight: "600",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "var(--primary-color)",
      ":hover": {
        backgroundColor: "var(--primary-color)",
        color: "white",
      },
    }),
  };

  const roleOptions = [
    { value: "admin", label: "Admin" },
    { value: "mod", label: "Moderator" },
    { value: "user", label: "User" },
    { value: "hr", label: "HR" },
    { value: "ops", label: "Operations" },
  ];

  if (loading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-container">
          <div className="spinner"></div>
        </div>
      </>
    );
  }

  if (error && !email) {
    return (
      <>
        <SideBar />
        <div className="dashboard-container">
          <div className="alert alert-error">{error}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        <div className="form-container">
          <div className="form-header">
            <h2 className="form-title">Update User</h2>
            <p className="form-subtitle">Edit user details and permissions</p>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h3 className="form-section-title">User Information</h3>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  placeholder="Enter Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  placeholder="Enter Full Name"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>User Roles</label>
                <Select
                  options={roleOptions}
                  value={roleOptions.filter((option) =>
                    roles.includes(option.value)
                  )}
                  onChange={handleRoleChange}
                  isMulti
                  styles={customStyles}
                  placeholder="Select roles..."
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate("/userdashboard")}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Update User
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UpdateUser;